import { useState, useEffect, useRef } from 'react';
import { Check } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const EMPTY = {
  categoryId: '', type: 'multiple_choice', level: 'medium',
  textDe: '', optionADe: '', optionBDe: '', optionCDe: '', optionDDe: '',
  textAl: '', optionAAl: '', optionBAl: '', optionCAl: '', optionDAl: '',
  correctOption: 'a', sourceRef: '',
};

const LEVELS = [
  { value: 'easy',   label: 'Leicht (30 Sek.)' },
  { value: 'medium', label: 'Mittel (20 Sek.)' },
  { value: 'hard',   label: 'Schwer (10 Sek.)' },
];

const OPTS = ['a', 'b', 'c', 'd'];

export default function QuestionModal({ question, onClose, onSaved, apiBase = '' }) {
  const { hasFeature } = useAuth();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [lang, setLang] = useState('de');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const imgInputRef = useRef();

  const isEdit = !!question?.id;
  const isTF = form.type === 'true_false';
  const isSuperAdmin = !!apiBase;
  const canUploadImage = !question?._readOnly && (isSuperAdmin || hasFeature('photo_questions'));

  useEffect(() => {
    const catUrl = apiBase ? `/superadmin/questions/categories` : '/questions/categories';
    api.get(catUrl).then(r => setCategories(r.data.categories));

    if (question?.id) {
      setForm({
        categoryId: question.category_id || '',
        type: question.type || 'multiple_choice',
        level: question.level || 'medium',
        textDe: question.text_de || '',
        optionADe: question.option_a_de || '',
        optionBDe: question.option_b_de || '',
        optionCDe: question.option_c_de || '',
        optionDDe: question.option_d_de || '',
        textAl: question.text_al || '',
        optionAAl: question.option_a_al || '',
        optionBAl: question.option_b_al || '',
        optionCAl: question.option_c_al || '',
        optionDAl: question.option_d_al || '',
        correctOption: question.correct_option || 'a',
        sourceRef: question.source_ref || '',
      });
    }
  }, [question, apiBase]);

  const set = (field, val) => {
    setForm(p => {
      const next = { ...p, [field]: val };
      // True/False: auto-fill options
      if (field === 'type' && val === 'true_false') {
        next.optionADe = 'Wahr'; next.optionBDe = 'Falsch'; next.optionCDe = ''; next.optionDDe = '';
        next.optionAAl = 'E vërtetë'; next.optionBAl = 'E gabuar'; next.optionCAl = ''; next.optionDAl = '';
        if (!['a','b'].includes(next.correctOption)) next.correctOption = 'a';
      }
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    const url = apiBase
      ? (isEdit ? `/superadmin/questions/${question.id}` : '/superadmin/questions')
      : (isEdit ? `/questions/${question.id}` : '/questions');
    const method = isEdit ? 'put' : 'post';
    try {
      const { data } = await api[method](url, form);
      let saved = data.question;

      // Upload image if one was selected (only for own questions via mosque admin)
      if (imageFile && canUploadImage) {
        try {
          const fd = new FormData();
          fd.append('image', imageFile);
          const uploadUrl = isSuperAdmin
            ? `/superadmin/questions/${saved.id}/image`
            : `/admin/questions/${saved.id}/image`;
          const imgRes = await api.post(uploadUrl, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          saved = { ...saved, image_path: imgRes.data.imagePath };
        } catch {
          // image upload failed silently — question still saved
        }
      }

      onSaved(saved, isEdit);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleImagePick = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    e.target.value = '';
  };

  const clearImage = () => {
    setImageFile(null);
    if (imagePreview) { URL.revokeObjectURL(imagePreview); setImagePreview(null); }
  };

  const DeField = ({ field, placeholder }) => (
    <input
      className="form-input"
      value={form[field] || ''}
      onChange={e => set(field, e.target.value)}
      placeholder={placeholder}
      required={!field.includes('C') && !field.includes('D')}
    />
  );

  const AlField = ({ field, placeholder }) => (
    <input
      className="form-input"
      value={form[field] || ''}
      onChange={e => set(field, e.target.value)}
      placeholder={placeholder}
      required={!field.includes('C') && !field.includes('D')}
    />
  );

  const optionsToShow = isTF ? ['a', 'b'] : OPTS;

  // Read-only view for central questions
  if (question?._readOnly) {
    const optKeys = question.type === 'true_false' ? ['a', 'b'] : OPTS;
    const correct = question.correct_option;
    return (
      <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="modal" style={{ maxWidth: 680, width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <h2 className="modal-title" style={{ margin: 0 }}>Zentrale Frage</h2>
              <span style={{ fontSize: 12, color: 'var(--text3)' }}>
                {question.category_name_de} · {LEVELS.find(l => l.value === question.level)?.label}
              </span>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
          </div>

          {/* Language tabs */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--border)' }}>
            {[['de', '🇩🇪 Deutsch'], ['al', '🇦🇱 Shqip']].map(([l, label]) => (
              <button key={l} type="button" onClick={() => setLang(l)} style={{
                padding: '8px 16px', fontSize: 13, fontWeight: lang === l ? 600 : 500,
                color: lang === l ? 'var(--primary)' : 'var(--text3)',
                background: 'none', border: 'none',
                borderBottom: lang === l ? '2px solid var(--primary)' : '2px solid transparent',
                marginBottom: -1, cursor: 'pointer',
              }}>
                {label}
              </button>
            ))}
          </div>

          {/* Photo first */}
          {question.image_path && (
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <img
                src={`/uploads/${question.image_path}`}
                alt=""
                style={{ maxWidth: '100%', maxHeight: 280, borderRadius: 10, objectFit: 'contain', border: '1px solid var(--border)' }}
              />
            </div>
          )}

          {/* Question text */}
          <div style={{
            background: 'var(--bg)', borderRadius: 10, padding: '16px 20px', marginBottom: 16,
            border: '1px solid var(--border)', fontSize: 17, fontWeight: 600, lineHeight: 1.5,
          }}>
            {lang === 'de' ? question.text_de : (question.text_al || question.text_de)}
          </div>

          {/* Options */}
          <div style={{ display: 'grid', gridTemplateColumns: question.type === 'true_false' ? '1fr 1fr' : '1fr 1fr', gap: 10, marginBottom: 16 }}>
            {optKeys.filter(o => question[`option_${o}_de`]).map(o => {
              const isCorrect = o === correct;
              const text = lang === 'de' ? question[`option_${o}_de`] : (question[`option_${o}_al`] || question[`option_${o}_de`]);
              return (
                <div key={o} style={{
                  padding: '12px 16px', borderRadius: 8,
                  background: isCorrect ? 'var(--primary-lt)' : 'var(--bg)',
                  border: `2px solid ${isCorrect ? 'var(--primary)' : 'var(--border)'}`,
                  color: isCorrect ? 'var(--primary-dk)' : 'var(--text)',
                  fontWeight: isCorrect ? 600 : 400, fontSize: 14,
                }}>
                  <span style={{ fontSize: 11, opacity: 0.6, display: 'block', marginBottom: 3 }}>
                    {o.toUpperCase()} {isCorrect ? <Check size={11} style={{ display: 'inline', verticalAlign: 'middle' }} /> : ''}
                  </span>
                  {text}
                </div>
              );
            })}
          </div>

          {question.source_ref && (
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
              Quelle: {question.source_ref}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            <button className="btn btn-primary" onClick={onClose}>Schließen</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 860, width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 className="modal-title" style={{ margin: 0 }}>
            {isEdit ? 'Frage bearbeiten' : 'Neue Frage erstellen'}
          </h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={submit}>
          {/* Top row: meta fields */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Kategorie *</label>
              <select className="form-select" value={form.categoryId} onChange={e => set('categoryId', e.target.value)} required>
                <option value="">Wählen...</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name_de}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Typ</label>
              <select className="form-select" value={form.type} onChange={e => set('type', e.target.value)}>
                <option value="multiple_choice">Multiple Choice</option>
                <option value="true_false">Wahr / Falsch</option>
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Schwierigkeitsgrad</label>
              <select className="form-select" value={form.level} onChange={e => set('level', e.target.value)}>
                {LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Richtige Antwort</label>
              <select className="form-select" value={form.correctOption} onChange={e => set('correctOption', e.target.value)}>
                {optionsToShow.map(o => <option key={o} value={o}>{o.toUpperCase()}</option>)}
              </select>
            </div>
          </div>

          {/* Language tabs */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--border)' }}>
            {[['de', '🇩🇪 Deutsch'], ['al', '🇦🇱 Shqip']].map(([l, label]) => (
              <button key={l} type="button" onClick={() => setLang(l)} style={{
                padding: '8px 16px', fontSize: 13, fontWeight: lang === l ? 600 : 500,
                color: lang === l ? 'var(--primary)' : 'var(--text3)',
                background: 'none', border: 'none',
                borderBottom: lang === l ? '2px solid var(--primary)' : '2px solid transparent',
                marginBottom: -1, cursor: 'pointer',
              }}>
                {label}
              </button>
            ))}
          </div>

          {/* German fields */}
          {lang === 'de' && (
            <div>
              <div className="form-group">
                <label className="form-label">Frage (Deutsch) *</label>
                <textarea className="form-textarea" rows={3} value={form.textDe}
                  onChange={e => set('textDe', e.target.value)} required
                  placeholder="Frage auf Deutsch eingeben..." />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isTF ? '1fr 1fr' : '1fr 1fr 1fr 1fr', gap: 10 }}>
                {optionsToShow.map(o => (
                  <div key={o} className="form-group" style={{ margin: 0 }}>
                    <label className="form-label" style={{ color: form.correctOption === o ? 'var(--primary)' : undefined }}>
                      Option {o.toUpperCase()} {form.correctOption === o ? <Check size={11} style={{ display: 'inline', verticalAlign: 'middle' }} /> : ''}
                    </label>
                    <DeField
                      field={`option${o.toUpperCase()}De`}
                      placeholder={isTF ? (o === 'a' ? 'Wahr' : 'Falsch') : `Option ${o.toUpperCase()}...`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Albanian fields */}
          {lang === 'al' && (
            <div>
              <div className="form-group">
                <label className="form-label">Pyetja (Shqip) *</label>
                <textarea className="form-textarea" rows={3} value={form.textAl}
                  onChange={e => set('textAl', e.target.value)} required
                  placeholder="Shkruani pyetjen shqip..." />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isTF ? '1fr 1fr' : '1fr 1fr 1fr 1fr', gap: 10 }}>
                {optionsToShow.map(o => (
                  <div key={o} className="form-group" style={{ margin: 0 }}>
                    <label className="form-label" style={{ color: form.correctOption === o ? 'var(--primary)' : undefined }}>
                      Opsioni {o.toUpperCase()} {form.correctOption === o ? <Check size={11} style={{ display: 'inline', verticalAlign: 'middle' }} /> : ''}
                    </label>
                    <AlField
                      field={`option${o.toUpperCase()}Al`}
                      placeholder={isTF ? (o === 'a' ? 'E vërtetë' : 'E gabuar') : `Opsioni ${o.toUpperCase()}...`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="form-group" style={{ marginTop: 16 }}>
            <label className="form-label">Quelle / Referenz (optional)</label>
            <input className="form-input" value={form.sourceRef}
              onChange={e => set('sourceRef', e.target.value)}
              placeholder="z.B. Sure 2:255, Sahih Bukhari 1234..." />
          </div>

          {canUploadImage && (
            <div className="form-group" style={{ marginTop: 16 }}>
              <label className="form-label">Bild (optional)</label>
              <input ref={imgInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImagePick} />
              {imagePreview ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <img src={imagePreview} alt="" style={{ height: 72, maxWidth: 160, objectFit: 'contain', borderRadius: 6, border: '1px solid var(--border)' }} />
                  <button type="button" className="btn btn-sm btn-danger" onClick={clearImage}>Bild entfernen</button>
                </div>
              ) : (isEdit && question?.image_path) ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <img src={`/uploads/${question.image_path}`} alt="" style={{ height: 72, maxWidth: 160, objectFit: 'contain', borderRadius: 6, border: '1px solid var(--border)' }} />
                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => imgInputRef.current.click()}>Bild ändern</button>
                </div>
              ) : (
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => imgInputRef.current.click()}>
                  + Bild hochladen
                </button>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Speichern...' : isEdit ? 'Änderungen speichern' : 'Frage erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
